import { moveToFolderNewFolderTooltipText } from './MoveToFolder.locstring.json';
import loc from 'owa-localize';
/* tslint:disable:jsx-no-lambda WI:47749 */
import * as React from 'react';
import { MoveToActionSource } from '../datapoints';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import getFolderNameToCountMap from '../helpers/getFolderNameToCountMap';
import getViewAllMenuItemsForSupportedContexts from './getViewAllMenuItemsForSupportedContexts';
import MoveToFoldersType from '../store/schema/MoveToFoldersType';
import MoveToMailboxContext from '../store/schema/MoveToMailboxContext';
import MoveToMenuItem from '../store/schema/MoveToMenuItem';
import moveToStore from '../store/store';
import NewFolderMenuItem from './NewFolderMenuItem';
import onFolderClickedInMoveToControl from '../actions/onFolderClickedInMoveToControl';

import { getFoldersForGivenContext } from '../helpers/getFoldersForMoveTo';
import { getMoveToNewFolderText, getSearchBoxLabelText } from '../helpers/moveToStringUtils';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { logUsage } from 'owa-analytics';
import { observer } from 'mobx-react';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import {
    getRootFolderIdForGivenContext,
    shouldShowMoveToLoadingSpinner,
} from '../helpers/moveToCommonUtils';
import {
    onSearchTextChanged,
    onViewAllMenuClicked,
    setIsFocusInSearchBox,
    onNewFolderMenuClicked,
} from '../actions/internalActions';
import {
    SearchBox,
    ISearchBoxStyles,
    ISearchBoxStyleProps,
} from 'office-ui-fabric-react/lib/SearchBox';
import {
    FocusZone,
    FocusZoneDirection,
    FocusZoneTabbableElements,
} from 'office-ui-fabric-react/lib/FocusZone';

const styles = require('./MoveToFolder.scss');
const classNames = require('classnames/bind').bind(styles);

const MAX_MOVETO_ITEMS = 8;

export interface MoveToFolderProps {
    actionSource: string;
    createNewFolder: (displayName: string, parentFolderId: string) => Promise<string>;
    dismissMenu: () => void;
    supportedMoveToMailboxContexts: MoveToMailboxContext[];
    onFolderClick: (folderId: string) => void;
    shouldShowSearchBox: boolean;
    getMenuItemsToPrepend?: (
        nonIconClassName: string,
        iconClassName: string,
        onAfterMenuClicked: () => void
    ) => JSX.Element[];
    getCustomIcon?: (folderId: string, showNonSpecialFolderIcon: boolean) => string;
    getCustomMenuItemsToAppend?: () => MoveToMenuItem[];
    defaultInAllFolders?: boolean;
    shouldHideNewFolder?: boolean;
    shouldStyleSelectedFolder?: boolean;
    selectedFolderId?: string;
    shouldShowRootFolder?: boolean;
    disableSelectedFolder?: boolean;
}

@observer
export default class MoveToFolder extends React.Component<MoveToFolderProps, {}> {
    private searchBoxRef;
    /**
     * Stores the callback to be called when user hits enter in the search box
     */
    private onSearchBoxEnterCallback: () => void;

    componentDidMount() {
        // Focus on search box after component mounts
        this.focusOnSearchBox();
    }

    render() {
        return (
            <FocusZone
                direction={FocusZoneDirection.bidirectional}
                handleTabKey={FocusZoneTabbableElements.all} // The menu is Tab accessible
                shouldInputLoseFocusOnArrowKey={(inputElement: HTMLInputElement) => {
                    // We want the up and down arrow keys on search input element to shift the focus to next and previous element respectively
                    // irrespective of the default behavior where FocusZone does not permit it if shifting focus is only doable using Tab keys
                    return true;
                }}>
                {this.renderMoveToFolderMenu()}
            </FocusZone>
        );
    }

    private focusOnSearchBox = () => {
        window.requestAnimationFrame(() => {
            if (this.searchBoxRef) {
                this.searchBoxRef.focus();
            }
        });
    };

    private dismissMenu = () => {
        this.props.dismissMenu();
    };

    private onSearchChange(event: React.ChangeEvent<HTMLInputElement>, findText: string) {
        onSearchTextChanged(findText);
    }

    private onSearchboxRender = () => {
        return (
            <SearchBox
                key={'searchMoveTo'}
                className={styles.moveToMenuSearchItemContainer}
                componentRef={ref => {
                    this.searchBoxRef = ref;
                }}
                value={moveToStore.findText}
                placeholder={getSearchBoxLabelText(moveToStore.moveToMailboxContext)}
                ariaLabel={getSearchBoxLabelText(moveToStore.moveToMailboxContext)}
                onEscape={this.onEscapeOnSearchBox}
                onChange={this.onSearchChange}
                onBlur={this.onBlurOnSearchBox}
                onFocus={this.onFocusOnSearchBox}
                onSearch={this.onEnterOnSearchBox}
                styles={this.getSearchBoxStyles}
            />
        );
    };

    private onFocusOnSearchBox(evt: React.FocusEvent<EventTarget>) {
        setIsFocusInSearchBox(true);
    }

    private onBlurOnSearchBox = (evt: React.FocusEvent<EventTarget>) => {
        setIsFocusInSearchBox(false);
        this.onSearchBoxEnterCallback = null;
    };

    private onEscapeOnSearchBox = (evt: React.MouseEvent<unknown>) => {
        // Dismiss the menu when user escapes while focus was on search box.
        // This needs to be handled separately than other menu items
        this.dismissMenu();
    };

    private onEnterOnSearchBox = (evt: React.MouseEvent<unknown>) => {
        if (this.onSearchBoxEnterCallback) {
            this.onSearchBoxEnterCallback();
        }
    };

    private onFolderSelect = (
        evt: React.MouseEvent<unknown> | React.KeyboardEvent<unknown>,
        folderId: string
    ) => {
        // No op if user selects a disabled folder
        if (this.props.disableSelectedFolder && this.props.selectedFolderId === folderId) {
            return;
        }
        this.dismissMenu();
        this.props.onFolderClick(folderId as string);
        onFolderClickedInMoveToControl(moveToStore.moveToMailboxContext, folderId);
        logUsage('TnS_MoveToClickFolder', [
            this.props.actionSource,
            moveToStore.moveToMailboxContext,
        ]);
    };

    private onCustomItemClicked = (
        evt: React.MouseEvent<unknown> | React.KeyboardEvent<unknown>,
        item: MoveToMenuItem
    ) => {
        evt.stopPropagation();
        this.dismissMenu();
        item.onClick();
    };

    /**
     * This method is called whenever "view all folders" of any mailbox is clicked
     * @param evt - click event
     * @param moveToMailboxContext - moveTo mailbox context
     */
    private onViewAll = (
        evt: React.MouseEvent<unknown>,
        moveToMailboxContext: MoveToMailboxContext,
        rootFolderId?: string
    ) => {
        // Required so as the menu does not dismiss when view all is clicked.
        // VSO: 22703 - Investigate why preventDefault is required when view all item is clicked in the move to context menu
        evt.stopPropagation();
        evt.preventDefault();
        onViewAllMenuClicked(moveToMailboxContext, rootFolderId);
        logUsage('TnS_MoveToViewAllFolders', [this.props.actionSource, moveToMailboxContext]);

        this.focusOnSearchBox();
    };

    private onCreateNewFolder = (
        folderName: string,
        moveToNewFolderActionSource: MoveToActionSource
    ) => {
        this.dismissMenu();
        const { moveToMailboxContext, moveToSharedFolderRootId } = moveToStore;

        // Get appropriate root folder id depending on the current moveToMailboxContext.
        // Except 'sharedFolder', all other contexts have static rootFolderId. But sharedFolder root Id is stored in local store.
        const rootFolderId =
            moveToMailboxContext === 'sharedFolder'
                ? moveToSharedFolderRootId
                : getRootFolderIdForGivenContext(moveToMailboxContext);
        this.props.createNewFolder(folderName, rootFolderId).then(folderId => {
            // VSO - 22606 - Handle waiting state when a new folder is created from move to menu
            this.props.onFolderClick(folderId);
        });

        logUsage('TnS_MoveToCreateNewFolder', [
            this.props.actionSource,
            moveToNewFolderActionSource,
            moveToStore.moveToMailboxContext,
        ]);
    };

    private addDividerMenu(items: JSX.Element[], key: string) {
        items.push(<div className={styles.moveToMenuDivider} />);
    }

    private renderMoveToFolderMenu(): JSX.Element[] {
        // We should allow MAX_MOVETO_ITEMS (excluding search/loading spinner) before the extended menu items
        let numMoveItemsToShow = MAX_MOVETO_ITEMS;
        let items: JSX.Element[] = [];
        let folders = null;

        // directly show all folders menu if defaultInAllFolders or moveToStore.shouldShowAllFolders is true
        const shouldShowAllFolders =
            this.props.defaultInAllFolders || moveToStore.shouldShowAllFolders;
        // Add search box
        if (this.props.shouldShowSearchBox) {
            items.push(this.onSearchboxRender());
            numMoveItemsToShow++;
        }

        if (shouldShowMoveToLoadingSpinner()) {
            items.push(
                <Spinner size={SpinnerSize.medium} className={styles.moveToLoadingSpinner} />
            );
            numMoveItemsToShow++;
        }

        let moveToFolder: MoveToFoldersType = { folders: [], hasExactMatch: false };
        const findText = moveToStore.findText.trim();

        // reset the onSearchBoxEnter call back
        this.onSearchBoxEnterCallback = null;

        if (
            moveToStore.moveToMailboxContext == 'primary' &&
            !shouldShowAllFolders &&
            this.props.getMenuItemsToPrepend
        ) {
            // get Inbox specific menu items (move to focus/other, inbox plus pivots, etc)
            let prependItems = this.props.getMenuItemsToPrepend(
                styles.nonIconMenuItem,
                styles.moveToMenuItemContainer,
                this.dismissMenu // onAfterMenuClicked
            );
            items.push(...prependItems);
            if (prependItems.length > 0) {
                this.addDividerMenu(items, 'moveto_pre_divider');
                numMoveItemsToShow++;
            }
        }

        // Get filtered folders for given context.
        moveToFolder = getFoldersForGivenContext(
            moveToStore.moveToMailboxContext,
            moveToStore.findText,
            shouldShowAllFolders,
            this.props.shouldShowRootFolder,
            this.props.disableSelectedFolder,
            this.props.selectedFolderId
        );
        folders = moveToFolder.folders;

        let isTopItemHighlighted = false;

        // Add folders.
        let folderNameCountMap = getFolderNameToCountMap(folders);
        for (let i = 0, len = folders.length; i < len; i++) {
            let folder = folders[i];
            let folderId = folder.folderId;
            let folderDisplayName = folder.displayName;
            let depth = folder.depth;
            let folderNameToShow =
                folderNameCountMap[folderDisplayName.toLowerCase()] > 1 &&
                folderIdToName(folder.parentFolderId) != 'msgfolderroot'
                    ? folder.parentFolderDisplayName + '/' + folderDisplayName
                    : folderDisplayName;
            let containerClass;
            const shouldShowSelectedMoveFolder =
                this.props.shouldStyleSelectedFolder && this.props.selectedFolderId === folderId;

            const shouldDisableFolder =
                this.props.disableSelectedFolder && this.props.selectedFolderId === folderId;

            // Highlight top item and store the callback
            if (i == 0 && this.shouldHighlightTopItem()) {
                containerClass = classNames(styles.highlightItem, styles.moveToMenuItemContainer);
                this.onSearchBoxEnterCallback = () => {
                    this.onFolderSelect(null, folderId);
                };
                isTopItemHighlighted = true;
            } else {
                containerClass = styles.moveToMenuItemContainer;
            }

            let matchText = '';
            let nonMatchText = folderNameToShow;

            if (
                findText &&
                folderNameToShow.toLocaleLowerCase().indexOf(findText.toLocaleLowerCase()) == 0
            ) {
                matchText = folderNameToShow.substr(0, findText.length);
                nonMatchText = folderNameToShow.substr(
                    findText.length,
                    folderNameToShow.length - 1
                );
            }

            items.push(
                <div
                    key={'moveTo' + folderId}
                    role={'menuitemcheckbox'}
                    data-is-focusable={true}
                    className={classNames(
                        shouldShowSelectedMoveFolder && styles.selected,
                        containerClass
                    )}
                    onClick={evt => this.onFolderSelect(evt, folderId)}
                    title={folderNameToShow}>
                    <Icon
                        className={classNames(styles.icon, shouldDisableFolder && styles.disabled)}
                        iconName={
                            this.props.getCustomIcon
                                ? this.props.getCustomIcon(folderId, true)
                                : null
                        }
                        styles={{ root: { paddingLeft: `${depth}em` } }}
                    />
                    <div
                        className={classNames(
                            styles.folderName,
                            shouldDisableFolder && styles.disabled
                        )}>
                        <span
                            className={classNames(
                                styles.folderMatchSuggestion,
                                shouldDisableFolder && styles.disabled
                            )}>
                            {matchText}
                        </span>
                        <span className={styles.folderSuggestion}>{nonMatchText}</span>
                    </div>
                </div>
            );
        }

        // Remove folders if there are more than numMoveItemsToShow folders
        // Only do this if we are not in search results and not in 'all folders' mode
        if (!findText && !shouldShowAllFolders && items.length > numMoveItemsToShow) {
            items.splice(numMoveItemsToShow, items.length);
        }

        // Add extended menus
        // Add divider if we are showing more than 1 (Search Box) items
        if (items.length > 1) {
            this.addDividerMenu(items, 'moveto_extendedMenu_divider');
        }

        // A boolean to decide if new folder option should be present or not.
        const newFolderShouldHide: boolean =
            moveToStore.moveToMailboxContext === 'sharedFolder' || this.props.shouldHideNewFolder;

        // Add create new menu when user had typed something but we could not find an exact match for it
        // Also, do not add it if the context is sharedFolder
        // Else add the new folder item
        if (!newFolderShouldHide) {
            if (findText && !moveToFolder.hasExactMatch) {
                this.addCreateNewMenu(items, isTopItemHighlighted);
            } else {
                this.addNewFolderMenu(items);
            }
        }

        const viewAllFoldersMenuItems = getViewAllMenuItemsForSupportedContexts(
            this.props.supportedMoveToMailboxContexts,
            folders,
            styles,
            this.onViewAll,
            shouldShowAllFolders
        );
        items.push(...viewAllFoldersMenuItems);

        if (this.props.getCustomMenuItemsToAppend) {
            const customItems = this.props.getCustomMenuItemsToAppend();
            if (customItems) {
                for (const customItem of customItems) {
                    items.push(
                        <div
                            key={'moveTo' + customItem.title}
                            role={'menuitemcheckbox'}
                            data-is-focusable={true}
                            className={styles.nonIconMenuItem}
                            onClick={evt => this.onCustomItemClicked(evt, customItem)}
                            title={customItem.title}>
                            <div className={styles.folderMenuItemContainer}>
                                <span className={styles.folderSuggestion}>
                                    {customItem.displayText}
                                </span>
                            </div>
                        </div>
                    );
                }
            }
        }

        return items;
    }

    private shouldHighlightTopItem(): boolean {
        return moveToStore.isFocusInSearchBox && moveToStore.findText.length > 0;
    }

    private addCreateNewMenu(items: JSX.Element[], isTopItemHighlighted: boolean) {
        // Add create new menu when user had typed something but we could not find an exact match for it
        let containerClass;

        // Highlight create new, store callback, if nothing is highlighted and we can still highlight
        if (!isTopItemHighlighted && this.shouldHighlightTopItem()) {
            containerClass = classNames(styles.highlightItem, styles.moveToMenuItemContainer);
            this.onSearchBoxEnterCallback = () => {
                this.onCreateNewFolder(moveToStore.findText, 'SearchBoxEnter');
            };
        } else {
            containerClass = styles.moveToMenuItemContainer;
        }

        items.push(
            <NewFolderMenuItem
                key={'createNewFolder'}
                containerClass={containerClass}
                isEditMode={false}
                createNew={this.onCreateNewFolder}
            />
        );
        this.addDividerMenu(items, 'moveto_createNewMenu_divider');
    }

    private addNewFolderMenu(items: JSX.Element[]) {
        if (moveToStore.newFolderViewState) {
            items.push(
                <NewFolderMenuItem
                    key={'newFolder_Edit'}
                    isEditMode={true}
                    createNew={this.onCreateNewFolder}
                />
            );
        } else {
            items.push(
                <div
                    key={'newFolder'}
                    className={styles.moveToMenuItemContainer}
                    data-is-focusable={true}
                    role={'menuitem'}
                    onClick={this.onNewFolderClicked}>
                    <div
                        title={loc(moveToFolderNewFolderTooltipText)}
                        className={classNames(styles.nonIconMenuItem)}>
                        {getMoveToNewFolderText(moveToStore.moveToMailboxContext)}
                    </div>
                </div>
            );
        }
        this.addDividerMenu(items, 'moveto_createNewMenu_divider');
    }

    private onNewFolderClicked(evt: React.MouseEvent<unknown>) {
        evt.stopPropagation();
        onNewFolderMenuClicked();
    }

    // Gets the custom styles for the search box to
    // disable animation and hiding of the search icon
    // setting the search icon color to neutral primary
    private getSearchBoxStyles(props: ISearchBoxStyleProps): Partial<ISearchBoxStyles> {
        const { palette } = props.theme;
        return {
            root: [
                {
                    paddingLeft: 0,
                },
            ],
            icon: [
                {
                    opacity: 1,
                    transition: 'none',
                },
            ],
            iconContainer: [
                {
                    width: 30,
                    color: palette.neutralPrimary,
                },
            ],
        };
    }
}
